/**
 * Customer Loyalty Distribution Chart
 *
 * Displays customer segments by visit frequency with period comparison
 */

import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { LockedChartCard } from "./locked-chart-card";
import { MetricTooltip } from "./metric-tooltip";

interface CustomerLoyaltyChartProps {
  current: Array<{
    segment: string;
    customerCount: number;
    percentage: number;
    totalRevenue: number;
  }> | null;
  previous: Array<{
    segment: string;
    customerCount: number;
    percentage: number;
    totalRevenue: number;
  }> | null;
}

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6"]; // red, amber, green, blue

const mockLoyaltyData = [
  { segment: "1 visit", customerCount: 25, percentage: 25, totalRevenue: 0 },
  { segment: "2-3 visits", customerCount: 35, percentage: 35, totalRevenue: 0 },
  { segment: "4-10 visits", customerCount: 25, percentage: 25, totalRevenue: 0 },
  { segment: "10+ visits", customerCount: 15, percentage: 15, totalRevenue: 0 },
];

export function CustomerLoyaltyChart({ current, previous }: CustomerLoyaltyChartProps) {
  const { t } = useTranslation("reports");

  // If data is null (basic tier), show locked version
  if (current === null || previous === null) {
    const mockContent = (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Mock Current Period */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-center">Current Period</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={mockLoyaltyData}
                  dataKey="customerCount"
                  nameKey="segment"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ percentage }) => `${percentage}%`}
                  labelLine={{ stroke: "currentColor" }}
                  style={{ fontSize: "10px" }}
                >
                  {mockLoyaltyData.map((entry, index) => (
                    <Cell key={`cell-mock-${entry.segment}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Mock Previous Period */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-center">Previous Period</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={mockLoyaltyData}
                  dataKey="customerCount"
                  nameKey="segment"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ percentage }) => `${percentage}%`}
                  labelLine={{ stroke: "currentColor" }}
                  style={{ fontSize: "10px" }}
                >
                  {mockLoyaltyData.map((entry, index) => (
                    <Cell
                      key={`cell-mock-prev-${entry.segment}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Mock Legend */}
        <div className="space-y-2">
          {mockLoyaltyData.map((segment, index) => (
            <div
              key={segment.segment}
              className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <div className="flex-1 grid grid-cols-3 gap-2 items-center">
                <p className="text-xs font-medium">{segment.segment}</p>
                <div className="text-xs text-muted-foreground text-center">00 → 00</div>
                <div className="text-xs text-right text-muted-foreground">+0%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <LockedChartCard
        title={t("charts.loyaltyDistribution.title")}
        tooltip={t("charts.loyaltyDistribution.tooltip")}
        mockContent={mockContent}
        height="auto"
      />
    );
  }

  const hasCurrentData = current && current.length > 0 && current.some((d) => d.customerCount > 0);
  const hasPreviousData =
    previous && previous.length > 0 && previous.some((d) => d.customerCount > 0);

  if (!hasCurrentData && !hasPreviousData) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        <CardHeader>
          <CardTitle>{t("charts.loyaltyDistribution.title")}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {t("charts.loyaltyDistribution.subtitle")}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px]">
            <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <CardHeader>
        <CardTitle className="flex items-center">
          {t("charts.loyaltyDistribution.title")}
          <MetricTooltip content={t("charts.loyaltyDistribution.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {t("charts.loyaltyDistribution.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Pie charts side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Current Period */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-center">{t("period.current")}</p>
              {hasCurrentData ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={current}
                      dataKey="customerCount"
                      nameKey="segment"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ percentage }) =>
                        percentage > 3 ? `${percentage.toFixed(0)}%` : ""
                      }
                      labelLine={{ stroke: "currentColor" }}
                      style={{ fontSize: "10px" }}
                      animationBegin={0}
                      animationDuration={800}
                    >
                      {current.map((entry, index) => (
                        <Cell
                          key={`cell-current-${entry.segment}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg p-3 shadow-lg">
                              <p className="font-semibold text-sm mb-2">{data.segment}</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.customers")}:
                                  </span>
                                  <span className="font-medium">{data.customerCount}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.percentage")}:
                                  </span>
                                  <span className="font-medium">{data.percentage.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.revenue")}:
                                  </span>
                                  <span className="font-medium">
                                    <PriceDisplay amount={data.totalRevenue} />
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-xs text-muted-foreground">{t("common.noData")}</p>
                </div>
              )}
            </div>

            {/* Previous Period */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-center">{t("period.previous")}</p>
              {hasPreviousData ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={previous}
                      dataKey="customerCount"
                      nameKey="segment"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ percentage }) =>
                        percentage > 3 ? `${percentage.toFixed(0)}%` : ""
                      }
                      labelLine={{ stroke: "currentColor" }}
                      style={{ fontSize: "10px" }}
                      animationBegin={0}
                      animationDuration={800}
                    >
                      {previous.map((entry, index) => (
                        <Cell
                          key={`cell-previous-${entry.segment}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg p-3 shadow-lg">
                              <p className="font-semibold text-sm mb-2">{data.segment}</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.customers")}:
                                  </span>
                                  <span className="font-medium">{data.customerCount}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.percentage")}:
                                  </span>
                                  <span className="font-medium">{data.percentage.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {t("common.revenue")}:
                                  </span>
                                  <span className="font-medium">
                                    <PriceDisplay amount={data.totalRevenue} />
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px]">
                  <p className="text-xs text-muted-foreground">{t("common.noData")}</p>
                </div>
              )}
            </div>
          </div>

          {/* Legend with comparison */}
          <div className="space-y-2">
            {current.map((segment, index) => {
              const prevSegment = previous.find((p) => p.segment === segment.segment);
              const change =
                prevSegment && prevSegment.customerCount > 0
                  ? ((segment.customerCount - prevSegment.customerCount) /
                      prevSegment.customerCount) *
                    100
                  : null;

              return (
                <div
                  key={segment.segment}
                  className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <div className="flex-1 grid grid-cols-3 gap-2 items-center">
                    <p className="text-xs font-medium">{segment.segment}</p>
                    <div className="text-xs text-muted-foreground text-center">
                      {prevSegment ? (
                        <>
                          {prevSegment.customerCount} → {segment.customerCount}
                        </>
                      ) : (
                        segment.customerCount
                      )}
                    </div>
                    <div className="text-xs text-right">
                      {change !== null && (
                        <span
                          className={
                            change > 0
                              ? "text-green-600 font-medium"
                              : change < 0
                                ? "text-red-600 font-medium"
                                : "text-muted-foreground"
                          }
                        >
                          {change > 0 ? "+" : ""}
                          {change.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
